import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResponsiveModalBottomSheet } from '@/components';
import Button from '@/components/Button';
import TextInput from '@/components/TextInput';
import type { CustomerPackage, SelectedPackage } from '@/models';
import { MinutesBadge } from '@/pages/components/Badge';

interface MinutesPackageBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  customerPackage: CustomerPackage | null;
  onConfirm: (selected: SelectedPackage) => Promise<void> | void;
  initialValue?: number;
}

const MinutesPackageBottomSheet: React.FC<MinutesPackageBottomSheetProps> = ({
  isOpen,
  onClose,
  customerPackage,
  onConfirm,
  initialValue,
}) => {
  const [minutes, setMinutes] = useState(
    initialValue !== undefined ? initialValue.toString() : '',
  );
  const { t } = useTranslation();
  useEffect(() => {
    if (isOpen) {
      setMinutes(initialValue !== undefined ? initialValue.toString() : '');
    }
  }, [isOpen, initialValue, customerPackage?.id]);
  if (!customerPackage) return null;
  const remaining = customerPackage.remainingValue;
  const maxLabel = t('package.minutes.maximum', { remaining });

  const handleConfirm = async () => {
    const valueNum = parseInt(minutes || '0', 10);
    if (valueNum <= 0 || valueNum > remaining) return;
    await onConfirm({ id: customerPackage.id, value: valueNum });
    onClose();
  };

  return (
    <ResponsiveModalBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      stickyFooter={
        <div className="px-4 pt-2 pb-4">
          <Button disabled={!minutes} onClick={handleConfirm}>
            {t('package.minutes.confirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 px-4 pt-2 pb-2">
        {/* Title & Badge */}
        <div className="flex flex-col gap-1">
          <h2 className="text-primary-text leading-6 font-medium">
            {customerPackage.name}
          </h2>
          <MinutesBadge />
        </div>

        {/* Remains & Expiry */}
        <div className="border-divider grid grid-cols-2 gap-4 border-t border-b py-4 text-sm">
          <div className="flex flex-col gap-0.5">
            <span className="text-secondary-text text-sm leading-5">
              {t('package.minutes.remains')}
            </span>
            <span className="text-primary-text leading-6 font-medium">
              {remaining}/{customerPackage.totalValue}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-secondary-text text-sm leading-5">
              {t('package.minutes.expiry')}
            </span>
            <span className="text-primary-text leading-6">
              {new Date(customerPackage.expiryDate).toLocaleDateString('en-GB')}
            </span>
          </div>
        </div>

        {/* Minutes input */}
        <div className="space-y-3">
          <div className="text-secondary-text text-center leading-6 font-semibold">
            {t('package.minutes.enterMinutes')}
          </div>
          <div className="flex items-center justify-center">
            <div className="relative h-9 w-[170px]">
              <TextInput
                size="small"
                placeholder={t('package.minutes.placeholder')}
                textAlign="center"
                inputMode="numeric"
                value={minutes}
                className="text-center font-semibold"
                style={{ fontSize: '16px' }}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, '');
                  if (!v) {
                    setMinutes('');
                    return;
                  }
                  const num = parseInt(v, 10);
                  if (num <= remaining) setMinutes(num.toString());
                }}
              />
            </div>
          </div>
          <div className="text-secondary-text text-center text-sm leading-5">
            {maxLabel}
          </div>
        </div>
      </div>
    </ResponsiveModalBottomSheet>
  );
};

export default MinutesPackageBottomSheet;
